import { EventConfig } from '@/types';

export const POST_MESSAGE_ACTIONS = {
  PLATFORM_ALERT: 'ALERT',
  PLATFORM_SYNC_USER: 'SYNC_USER',
  PLATFORM_SSO_EXPIRED_TOKEN: 'EXPIRED_TOKEN'
};

export const ONBOARDING_SESSION_KEY = 'X-Onboarding-Session-Key';
export const MOBILE_SESSION_KEY = 'OpenInMobile';

// event tracking
export const SYSTEM = 'app_idverify' as const;

export enum EVENT {
  KNOW_HOW_TO_GET_BUTTON_VIEW = 'idverify.know_how_to_get_button.view',
  KNOW_HOW_TO_GET_BUTTON_CLICKED = 'idverify.know_how_to_get_button.clicked',
  START_REGISTRATION_BUTTON_CLICKED = 'idverify.start_registration_button.clicked',
  START_REGISTRATION_PAGE_VIEW = 'idverify.start_registration_page.view',
  TRY_AGAIN_QRCODE_CLICKED = 'idverify.try_again_qrcode.clicked',
  EXIT_BUTTON_CLICKED = 'idverify.exit_button.clicked',
  QRCODE_PAGE_ERROR_VIEW = 'idverify.qrcode_page_error.view',
  QRCODE_PAGE_OPENED_VIEW = 'idverify.qrcode_page_opened_view.view',
  QRCODE_CLOSE_BUTTON_CLICKED = 'idverify.qrcode_close_button.clicked',
  START_QRCODE_VIEW = 'idverify.start_qrcode.view',
  FIRST_STEP_ERROR_EXIT_VIEW = 'idverify.first_step_error_exit.view',
  FIRST_STEP_ERROR_VIEW = 'idverify.first_step_error.view',
  START_FACIAL_RECOGNITION_PAGE_VIEW = 'idverify.start_facial_recognition_page.view',
  START_FIRST_STEP_BUTTON_CLICKED = 'idverify.start_first_step_button.clicked',
  NO_ACCESS_CAMERA_BUTTON_CLICKED = 'idverify.no_access_camera_button.clicked',
  CLOSE_MODAL_BUTTON_CLICKED = 'idverify.close_modal_button.clicked',
  DOCUMENTS_REGISTRATION_PAGE_VIEW = 'idverify.documents_registration_page.view',
  REGISTRATION_COMPLETED_SUCCESSFULLY_PAGE_VIEW = 'idverify.registration_completed_successfully_page.view',
  REGISTRATION_COMPLETED_SUCCESSFULLY_DESKTOP_PAGE_VIEW = 'idverify.registration_completed_successfully_desktop_page.view',
  CONSULT_DOCUMENT_ANALYSIS_PAGE_VIEW = 'idverify.consult_document_analysis_page.view',
  REGISTER_BANK_ACCOUNT_BUTTON_CLICKED = 'idverify.register_bank_account_button.clicked',
  REGISTER_DOCUMENTS_AGAIN_BUTTON_CLICKED = 'idverify.register_documents_again_button.clicked',
  INVALID_DOCUMENT_ERROR_PAGE_VIEW = 'idverify.invalid_document_error_page.view',
  TRY_AGAIN_BUTTON_CLICKED = 'idverify.try_again_button.clicked',
  FACIAL_RECOGNITION_SUCCESS_PAGE_VIEW = 'idverify.facial_recognition_success_page.view',
  DOCUMENT_SENT_SUCCESSFULLY_PAGE_VIEW = 'idverify.document_sent_successfully_page.view',
  LINK_OPEN_ON_MOBILE_CLICKED = 'idverify.link_open_on_mobile.click',
  TIME_EXPIRED_VIEW = 'idverify.time_expired.view',
  SESSION_EXPIRED_VIEW = 'idverify.session_expired_view.view',
  QRCODE_REGENERATED = 'idverify.qrcode_regenerated.view',
  FINISH_FLOW_REVIEW_SUCCESSFULLY_CLICK = 'idverify.finish_flow_review_successfully.click',
  FACE_RECOGNITION_ERROR = 'idverify.face_recognition.error',
  CAMERA_PERMISSION_ERROR = 'idverify.camera_permission.error',
  DOCUMENT_UPLOAD_ERROR = 'idverify.document_upload.error',
  NETWORK_ERROR = 'idverify.network.error',
  SDK_INITIALIZATION_ERROR = 'idverify.sdk_initialization.error',
  DOCUMENT_VALIDATION_ERROR = 'idverify.document_validation.error',
  FACE_LIVENESS_ERROR = 'idverify.face_liveness.error',
  DOCUMENT_QUALITY_ERROR = 'idverify.document_quality.error',
  NOTIFICATION_TRY_AGAIN_VIEW = 'idverify.notification_try_again.view',
  NOTIFICATION_OCR_ERROR_VIEW = 'idverify.notification_ocr_error.view',
  NOTIFICATION_AUTHORIZATION_VIEW = 'idverify.notification_authorization.view',
  NOTIFICATION_ERROR_RETRY_CLICK = 'idverify.notification_error_retry.click'
}

export const EVENT_CONFIG: Record<(typeof EVENT)[keyof typeof EVENT], EventConfig> = {
  [EVENT.KNOW_HOW_TO_GET_BUTTON_VIEW]: {
    eventName: EVENT.KNOW_HOW_TO_GET_BUTTON_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.KNOW_HOW_TO_GET_BUTTON_CLICKED]: {
    eventName: EVENT.KNOW_HOW_TO_GET_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.START_REGISTRATION_BUTTON_CLICKED]: {
    eventName: EVENT.START_REGISTRATION_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.START_REGISTRATION_PAGE_VIEW]: {
    eventName: EVENT.START_REGISTRATION_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.TRY_AGAIN_QRCODE_CLICKED]: {
    eventName: EVENT.TRY_AGAIN_QRCODE_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.EXIT_BUTTON_CLICKED]: {
    eventName: EVENT.EXIT_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.QRCODE_PAGE_ERROR_VIEW]: {
    eventName: EVENT.QRCODE_PAGE_ERROR_VIEW,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.QRCODE_CLOSE_BUTTON_CLICKED]: {
    eventName: EVENT.QRCODE_CLOSE_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.START_QRCODE_VIEW]: {
    eventName: EVENT.START_QRCODE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.FIRST_STEP_ERROR_EXIT_VIEW]: {
    eventName: EVENT.FIRST_STEP_ERROR_EXIT_VIEW,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.FIRST_STEP_ERROR_VIEW]: {
    eventName: EVENT.FIRST_STEP_ERROR_VIEW,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.START_FACIAL_RECOGNITION_PAGE_VIEW]: {
    eventName: EVENT.START_FACIAL_RECOGNITION_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.START_FIRST_STEP_BUTTON_CLICKED]: {
    eventName: EVENT.START_FIRST_STEP_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.NO_ACCESS_CAMERA_BUTTON_CLICKED]: {
    eventName: EVENT.NO_ACCESS_CAMERA_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.CLOSE_MODAL_BUTTON_CLICKED]: {
    eventName: EVENT.CLOSE_MODAL_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.DOCUMENTS_REGISTRATION_PAGE_VIEW]: {
    eventName: EVENT.DOCUMENTS_REGISTRATION_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.REGISTRATION_COMPLETED_SUCCESSFULLY_PAGE_VIEW]: {
    eventName: EVENT.REGISTRATION_COMPLETED_SUCCESSFULLY_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.REGISTRATION_COMPLETED_SUCCESSFULLY_DESKTOP_PAGE_VIEW]: {
    eventName: EVENT.REGISTRATION_COMPLETED_SUCCESSFULLY_DESKTOP_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.CONSULT_DOCUMENT_ANALYSIS_PAGE_VIEW]: {
    eventName: EVENT.CONSULT_DOCUMENT_ANALYSIS_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.REGISTER_BANK_ACCOUNT_BUTTON_CLICKED]: {
    eventName: EVENT.REGISTER_BANK_ACCOUNT_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.REGISTER_DOCUMENTS_AGAIN_BUTTON_CLICKED]: {
    eventName: EVENT.REGISTER_DOCUMENTS_AGAIN_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.INVALID_DOCUMENT_ERROR_PAGE_VIEW]: {
    eventName: EVENT.INVALID_DOCUMENT_ERROR_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.TRY_AGAIN_BUTTON_CLICKED]: {
    eventName: EVENT.TRY_AGAIN_BUTTON_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.FACIAL_RECOGNITION_SUCCESS_PAGE_VIEW]: {
    eventName: EVENT.FACIAL_RECOGNITION_SUCCESS_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.DOCUMENT_SENT_SUCCESSFULLY_PAGE_VIEW]: {
    eventName: EVENT.DOCUMENT_SENT_SUCCESSFULLY_PAGE_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.QRCODE_PAGE_OPENED_VIEW]: {
    eventName: EVENT.QRCODE_PAGE_OPENED_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.TIME_EXPIRED_VIEW]: {
    eventName: EVENT.TIME_EXPIRED_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.SESSION_EXPIRED_VIEW]: {
    eventName: EVENT.SESSION_EXPIRED_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.LINK_OPEN_ON_MOBILE_CLICKED]: {
    eventName: EVENT.LINK_OPEN_ON_MOBILE_CLICKED,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.QRCODE_REGENERATED]: {
    eventName: EVENT.QRCODE_REGENERATED,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.FINISH_FLOW_REVIEW_SUCCESSFULLY_CLICK]: {
    eventName: EVENT.FINISH_FLOW_REVIEW_SUCCESSFULLY_CLICK,
    eventScreen: 'idverify',
    eventType: 'click',
  },
  [EVENT.FACE_RECOGNITION_ERROR]: {
    eventName: EVENT.FACE_RECOGNITION_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.CAMERA_PERMISSION_ERROR]: {
    eventName: EVENT.CAMERA_PERMISSION_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.DOCUMENT_UPLOAD_ERROR]: {
    eventName: EVENT.DOCUMENT_UPLOAD_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.NETWORK_ERROR]: {
    eventName: EVENT.NETWORK_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.SDK_INITIALIZATION_ERROR]: {
    eventName: EVENT.SDK_INITIALIZATION_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.DOCUMENT_VALIDATION_ERROR]: {
    eventName: EVENT.DOCUMENT_VALIDATION_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.FACE_LIVENESS_ERROR]: {
    eventName: EVENT.FACE_LIVENESS_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.DOCUMENT_QUALITY_ERROR]: {
    eventName: EVENT.DOCUMENT_QUALITY_ERROR,
    eventScreen: 'idverify',
    eventType: 'error',
  },
  [EVENT.NOTIFICATION_TRY_AGAIN_VIEW]: {
    eventName: EVENT.NOTIFICATION_TRY_AGAIN_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.NOTIFICATION_OCR_ERROR_VIEW]: {
    eventName: EVENT.NOTIFICATION_OCR_ERROR_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.NOTIFICATION_AUTHORIZATION_VIEW]: {
    eventName: EVENT.NOTIFICATION_AUTHORIZATION_VIEW,
    eventScreen: 'idverify',
    eventType: 'view',
  },
  [EVENT.NOTIFICATION_ERROR_RETRY_CLICK]: {
    eventName: EVENT.NOTIFICATION_ERROR_RETRY_CLICK,
    eventScreen: 'idverify',
    eventType: 'click',
  }
};

export type EventKey = typeof EVENT[keyof typeof EVENT];
